import { useEffect, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';

/**
 * Mounted as the element of the `/orgs/:orgSlug/*` parent route. Looks up the
 * URL slug against the user's memberships, syncs the active organization
 * to the matching membership, and renders an `<Outlet />` for child routes.
 *
 * **The URL is the org preference.** When the slug differs from the active
 * org we call `setActiveOrganizationId`, which both updates in-memory state
 * and writes `localStorage`. Visiting `/runloop/agents` therefore makes
 * `runloop` the persisted preference for this browser — the next visit to
 * `/` redirects to that org's agents screen via `OrgRootRedirect`. Bookmarks,
 * shared links, and browser history are all valid ways to switch orgs.
 *
 * This behavior is intentional and load-bearing — do not gate the write on
 * "explicit user action". A previous attempt at that (PR #832, reverted in
 * #837) caused URL navigation to no-op the preference, leaving users on the
 * wrong org after using shared links.
 *
 * If the slug doesn't match any membership the user has access to we
 * still re-fetch `/auth/me` once before giving up. The in-memory
 * memberships are a snapshot from app mount, so a tab that's been open
 * since before the user was added to an org would otherwise render
 * `NotFoundPage` for every child route until a hard refresh. After the
 * one-shot refresh we either render the matching outlet or fall back to
 * 404 — never silently fall through to a different org (which would mask
 * access errors and confuse caching).
 */
export function OrgSlugResolver() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { memberships, activeOrganizationId, setActiveOrganizationId, loading, refreshSession } =
    useAuth();

  const match = memberships.find((m) => m.organization.slug === orgSlug) ?? null;

  useEffect(() => {
    if (match && match.organization.id !== activeOrganizationId) {
      setActiveOrganizationId(match.organization.id);
    }
  }, [match, activeOrganizationId, setActiveOrganizationId]);

  // One-shot self-heal: if the URL slug isn't in the cached memberships,
  // re-fetch `/auth/me` once per slug before falling back to 404. This
  // covers the case where the user was added to a new org after the tab
  // was opened — without this, every child route renders `NotFoundPage`
  // until the user hard-refreshes the page.
  //
  // `attemptedSlug` is state (not a ref) so the derived `pendingRefresh`
  // value below changes a re-render once we kick off the refreshSession, which
  // keeps the loading-vs-404 branches deterministic. `refreshFiredFor`
  // is a ref used *only inside the effect* to absorb React StrictMode's
  // double-invocation — the captured `pendingRefresh` from the first
  // render is still `true` on the second run, so without a ref check
  // we'd hit `/auth/me` twice on every mount in dev.
  const refreshFiredFor = useRef<Set<string>>(new Set());
  const [attemptedSlug, setAttemptedSlug] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const slugIsHealable = !!orgSlug && !match && !loading;
  const pendingRefresh = slugIsHealable && attemptedSlug !== orgSlug && !refreshing;

  useEffect(() => {
    if (!pendingRefresh) return;
    const slug = orgSlug ?? '';
    if (refreshFiredFor.current.has(slug)) return;
    refreshFiredFor.current.add(slug);
    setAttemptedSlug(orgSlug ?? null);
    setRefreshing(true);
    void refreshSession().finally(() => setRefreshing(false));
  }, [pendingRefresh, orgSlug, refreshSession]);

  // Show the spinner while:
  //  - the initial auth bootstrap is in flight, or
  //  - we've just queued a one-shot refresh for an unknown slug (covers
  //    the gap between render and the effect firing, so the user never
  //    sees a flash of `NotFoundPage` before the refresh completes), or
  //  - the refresh is in flight.
  if (loading || refreshing || pendingRefresh) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground"
        data-testid="org-slug-resolver-loading"
      >
        Loading&hellip;
      </div>
    );
  }

  if (!orgSlug || !match) {
    return <NotFoundPage />;
  }

  // Hold child rendering until the active org id in `useAuth` actually
  // matches the slug in the URL. The effect above writes localStorage and
  // dispatches the state change, but those happen *after* commit — without
  // this gate the child tree's first render fires its queries with the
  // previous org's `x-organization-id` header, briefly serving the wrong
  // org's data into a globally-keyed React Query cache. Showing a spinner
  // for one render cycle keeps the cache clean and matches what the user
  // already sees during initial auth bootstrap.
  if (match.organization.id !== activeOrganizationId) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground"
        data-testid="org-slug-resolver-syncing"
      >
        Loading&hellip;
      </div>
    );
  }

  return <Outlet />;
}
